// Centralized SEO metadata source for route-aware head management.
// The real site origin is resolved from the VITE_SITE_URL environment variable.
// Fallback to the known project domain only when the variable is not set.

export const SITE_URL: string =
  (import.meta.env.VITE_SITE_URL as string | undefined) ||
  "https://kyoto-gomisyori.com";

export const HOME_HERO_IMAGE: string =
  "https://readdy.ai/api/search-image?query=Two%20Japanese%20male%20workers%20in%20their%20late%2020s%20to%20early%2040s%20wearing%20clean%20navy%20blue%20work%20uniforms%20with%20company%20logo%20patch%20smiling%20sincerely%20while%20lifting%20a%20large%20cardboard%20box%20together%20beside%20a%20white%20light%20commercial%20truck%20with%20open%20cargo%20door%20in%20a%20narrow%20quiet%20Kyoto%20residential%20alley%20bordered%20by%20traditional%20dark%20wooden%20machiya%20townhouses%20with%20tiled%20roofs%20lush%20green%20potted%20plants%20and%20stone%20walls%20warm%20natural%20afternoon%20sunlight%20streaming%20through%20the%20street%20creating%20soft%20shadows%20documentary%20style%20candid%20workplace%20photography%20shot%20on%20professional%20DSLR%20camera%20with%20shallow%20depth%20of%20field%20subjects%20occupy%20right%20portion%20of%20frame%20leaving%20spacious%20clean%20background%20on%20left%20side%20highly%20photorealistic%20genuine%20unposed%20expression%20no%20artificial%20studio%20lighting%20no%20AI%20illustration%20look%20authentic%20Japanese%20waste%20collection%20service%20crew%20portrait&width=1600&height=900&seq=ats-hero-v19-2001&orientation=landscape";

export const FUYOUHIN_HERO_IMAGE: string =
  "https://readdy.ai/api/search-image?query=Clean%20white%20light%20commercial%20truck%20with%20open%20cargo%20door%20parked%20in%20front%20of%20a%20traditional%20Japanese%20Kyoto%20machiya%20townhouse%2C%20uniformed%20professional%20worker%20gently%20loading%20neatly%20tied%20cardboard%20boxes%20and%20a%20small%20sofa%20into%20the%20truck%2C%20warm%20natural%20afternoon%20sunlight%2C%20quiet%20residential%20alley%20with%20green%20potted%20plants%20and%20stone%20walls%2C%20calm%20trustworthy%20atmosphere%2C%20warm%20neutral%20beige%20tones%2C%20editorial%20documentary%20photography%2C%20shallow%20depth%20of%20field%2C%20soft%20natural%20shadows%2C%20authentic%20Japanese%20junk%20removal%20service%20scene%2C%20high%20detail%20photorealistic%20composition&width=1600&height=900&seq=fuyouhin-hero-01&orientation=landscape";

export const IHIN_HERO_IMAGE: string =
  "https://readdy.ai/api/search-image?query=Tranquil%20Japanese%20estate%20cleanup%20room%20in%20a%20traditional%20Kyoto%20machiya%20house%20with%20neatly%20organized%20cardboard%20boxes%20and%20carefully%20folded%20kimono%20garments%20placed%20on%20a%20clean%20tatami%20floor%20a%20small%20wooden%20Buddhist%20altar%20and%20framed%20family%20photographs%20arranged%20respectfully%20on%20a%20low%20table%20soft%20warm%20natural%20window%20light%20streaming%20through%20shoji%20screens%20no%20people%20visible%20calm%20dignified%20serene%20atmosphere%20warm%20muted%20beige%20and%20cream%20tones%20editorial%20documentary%20photography%20simple%20clean%20uncluttered%20background%20high%20detail%20photorealistic%20composition&width=1600&height=900&seq=ihin-hero-v01&orientation=landscape";

export const GOMIYASHIKI_HERO_IMAGE: string =
  "https://readdy.ai/api/search-image?query=Clean%20and%20tidy%20Japanese%20apartment%20room%20in%20Kyoto%20after%20professional%20hoarding%20cleanup%20freshly%20cleaned%20wooden%20floor%20and%20organized%20storage%20boxes%20neatly%20arranged%20bright%20natural%20daylight%20through%20a%20large%20window%20a%20few%20remaining%20boxes%20of%20sorted%20belongings%20in%20the%20center%20right%20no%20people%20visible%20fresh%20sanitary%20reassuring%20atmosphere%20warm%20neutral%20beige%20and%20white%20tones%20editorial%20documentary%20photography%20simple%20clean%20background%20high%20detail%20photorealistic%20composition&width=1600&height=900&seq=gomi-yashiki-hero-v01&orientation=landscape";

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogType: string;
  ogTitle: string;
  ogDescription: string;
  ogLocale: string;
  ogUrl: string;
  ogSiteName: string;
  ogImage?: string;
  twitterCard: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage?: string;
}

const homeSeo: SeoData = {
  title: "京都の不用品回収・遺品整理・ゴミ屋敷清掃｜京都ゴミ処分センター",
  description:
    "京都の不用品回収・粗大ゴミ処分なら京都ゴミ処分センターへ。家具・家電・大型ゴミの回収から、遺品整理、ゴミ屋敷清掃、残置物撤去まで幅広く対応。最短即日対応・見積無料。京都市内を中心に京都府全域で、分別・搬出・処分まで丁寧に一括対応いたします。",
  keywords:
    "京都 不用品回収,京都 遺品整理,京都 ゴミ屋敷清掃,京都 不用品処分,京都 残置物撤去,京都 産廃回収",
  canonical: `${SITE_URL}/`,
  ogType: "website",
  ogTitle: "京都の不用品回収・遺品整理・ゴミ屋敷清掃｜京都ゴミ処分センター",
  ogDescription:
    "京都府全域対応。即日対応・丁寧な作業・法人案件も歓迎。軽トラ積み放題20,000円〜。京都 不用品回収・京都 遺品整理・京都 ゴミ屋敷清掃はお任せください。",
  ogLocale: "ja_JP",
  ogSiteName: "京都ゴミ処分センター",
  ogUrl: `${SITE_URL}/`,
  ogImage: HOME_HERO_IMAGE,
  twitterCard: "summary_large_image",
  twitterTitle: "京都の不用品回収・遺品整理・ゴミ屋敷清掃｜京都ゴミ処分センター",
  twitterDescription:
    "京都府全域対応。即日対応・丁寧な作業・法人案件も歓迎。軽トラ積み放題20,000円〜。京都 不用品回収・京都 遺品整理・京都 ゴミ屋敷清掃はお任せください。",
  twitterImage: HOME_HERO_IMAGE,
};

const fuyouhinSeo: SeoData = {
  title: "京都の不用品回収・粗大ゴミ処分｜京都ゴミ処分センター",
  description:
    "京都の不用品回収・粗大ゴミ処分なら京都ゴミ処分センター。家具・家電1点からお部屋丸ごとまで最短即日対応、見積無料。分別・搬出込みで京都府全域を丁寧に対応します。引越しの一括回収・大型ゴミ・法人案件も歓迎。",
  keywords: "京都 不用品回収,京都 不用品処分,京都 粗大ゴミ,不用品回収,粗大ゴミ処分",
  canonical: `${SITE_URL}/fuyouhin-kaisyu/`,
  ogType: "website",
  ogTitle: "京都の不用品回収・粗大ゴミ処分｜京都ゴミ処分センター",
  ogDescription:
    "京都の不用品回収・粗大ゴミ処分なら京都ゴミ処分センター。家具・家電1点からお部屋丸ごとまで最短即日対応、見積無料。京都府全域に対応します。",
  ogLocale: "ja_JP",
  ogSiteName: "京都ゴミ処分センター",
  ogUrl: `${SITE_URL}/fuyouhin-kaisyu/`,
  ogImage: FUYOUHIN_HERO_IMAGE,
  twitterCard: "summary_large_image",
  twitterTitle: "京都の不用品回収・粗大ゴミ処分｜京都ゴミ処分センター",
  twitterDescription:
    "京都の不用品回収・粗大ゴミ処分なら京都ゴミ処分センター。家具・家電1点からお部屋丸ごとまで最短即日対応、見積無料。京都府全域に対応します。",
  twitterImage: FUYOUHIN_HERO_IMAGE,
};

const ihinSeo: SeoData = {
  title: "京都の遺品整理・遺品整理業者｜京都ゴミ処分センター",
  description:
    "京都の遺品整理・遺品整理業者なら京都ゴミ処分センター。京都市内を中心に京都府全域で対応。貴重品・思い出の品の仕分けから供養、形見分けまで一貫サポート。遺品整理の料金は明確、見積無料・即日対応。",
  keywords:
    "京都 遺品整理,京都市 遺品整理,遺品整理 京都,遺品整理 業者 京都,遺品整理 料金",
  canonical: `${SITE_URL}/ihin-seiri/`,
  ogType: "website",
  ogTitle: "京都の遺品整理・遺品整理業者｜京都ゴミ処分センター",
  ogDescription:
    "京都府全域対応。遺品整理士在籍、貴重品の仕分けから供養・形見分けまで一貫サポート。見積無料。京都 遺品整理・遺品整理業者なら京都ゴミ処分センターへ。",
  ogLocale: "ja_JP",
  ogSiteName: "京都ゴミ処分センター",
  ogUrl: `${SITE_URL}/ihin-seiri/`,
  ogImage: IHIN_HERO_IMAGE,
  twitterCard: "summary_large_image",
  twitterTitle: "京都の遺品整理・遺品整理業者｜京都ゴミ処分センター",
  twitterDescription:
    "京都府全域対応。遺品整理士在籍、貴重品の仕分けから供養・形見分けまで一貫サポート。見積無料。",
  twitterImage: IHIN_HERO_IMAGE,
};

const gomiYashikiSeo: SeoData = {
  title: "京都のゴミ屋敷清掃・ゴミ屋敷片付け｜京都ゴミ処分センター",
  description:
    "京都のゴミ屋敷清掃・ゴミ屋敷片付けなら京都ゴミ処分センター。京都市内を中心に京都府全域で対応。堆積ゴミの搬出・消臭・除菌・害虫駆除まで一気に解決。近隣配慮・プライバシー厳守、見積無料・即日対応。",
  keywords:
    "京都 ゴミ屋敷,京都市 ゴミ屋敷,ゴミ屋敷 清掃 京都,ゴミ屋敷 片付け 京都,ゴミ屋敷 清掃 料金",
  canonical: `${SITE_URL}/gomi-yashiki/`,
  ogType: "website",
  ogTitle: "京都のゴミ屋敷清掃・ゴミ屋敷片付け｜京都ゴミ処分センター",
  ogDescription:
    "京都府全域対応。堆積ゴミの搬出から消臭・除菌・害虫駆除まで一気に解決。プライバシー厳守。見積無料。京都 ゴミ屋敷清掃・ゴミ屋敷片付けは京都ゴミ処分センターへ。",
  ogLocale: "ja_JP",
  ogSiteName: "京都ゴミ処分センター",
  ogUrl: `${SITE_URL}/gomi-yashiki/`,
  ogImage: GOMIYASHIKI_HERO_IMAGE,
  twitterCard: "summary_large_image",
  twitterTitle: "京都のゴミ屋敷清掃・ゴミ屋敷片付け｜京都ゴミ処分センター",
  twitterDescription:
    "京都府全域対応。堆積ゴミの搬出から消臭・除菌・害虫駆除まで一気に解決。見積無料。",
  twitterImage: GOMIYASHIKI_HERO_IMAGE,
};

const seoByPath: Record<string, SeoData> = {
  "/": homeSeo,
  "/fuyouhin-kaisyu": fuyouhinSeo,
  "/ihin-seiri": ihinSeo,
  "/gomi-yashiki": gomiYashikiSeo,
};

export function normalizePath(pathname: string): string {
  if (pathname === "/") return "/";
  return pathname.replace(/\/+$/, "");
}

export function getSeoData(pathname: string): SeoData {
  return seoByPath[normalizePath(pathname)] || homeSeo;
}